/* eslint-disable no-useless-escape */
import * as React from 'react';
import {
	Modal,
	Button,
	Grid,
	XFilledIcon
} from 'react-unity';
import './RuleModal.css';
import TextAreaField from '../../../common/form-controls/TextareaField';
import ToggleField from '../../../common/form-controls/ToggleField';
import TextInputField from '../../../common/form-controls/TextInputField';
import { AbstractRulesForm, IRuleStateForm, RulesFormProps } from './AbstractRulesForm';
import { RuleAction } from '../../../../models/enums/DNDR/RuleAction';
import { RuleRegion } from '../../../../models/enums/DNDR/RuleRegion';
import { TrafficDirection } from '../../../../models/enums/DNDR/TrafficDirection';
import { VirtualNetwork } from '../../../../models/entities/AzureResources/VirtualNetwork';
import SelectField from '../../../common/form-controls/SelectField';
import MultipleSelectField from '../../../common/form-controls/MultipleSelectField';
import { RuleOperation } from '../../../../models/enums/DNDR/RuleOperation';
import { DoNotDecryptRequestsDirection } from '../../../../models/enums/DNDR/DoNotDecryptRequestsDirection';
import DoNotDecryptRule from '../../../../models/entities/DoNotDecryptRule';
import { Address4 } from 'ip-address';

interface RuleModalSubToOnPremAddNewProps extends RulesFormProps {
	subscriptionRequestName: string;
	onConfirmMultiple?: (rules: DoNotDecryptRule[]) => void;
}

interface SubToOnPremAddNewRuleStateForm extends IRuleStateForm {
	name: string;
	trafficDirection: TrafficDirection;
	protocol: string;
	destinationURLs: string;
	// Override to string[] for multi-select
	sourceIPsArray: string[];
	destinationIPsArray: string[];
	destinationPortsArray: string[];
}

interface RuleModalSubToOnPremAddNewState {
	stateForm: SubToOnPremAddNewRuleStateForm;
}

export default class RuleModalSubToOnPremAddNew extends AbstractRulesForm<
	RuleModalSubToOnPremAddNewProps,
	RuleModalSubToOnPremAddNewState
> {

	constructor(props: RuleModalSubToOnPremAddNewProps) {
		super(props);
		this.state = {
			stateForm: this.getDefaultForm() as SubToOnPremAddNewRuleStateForm,
		};
		this.initFormModel();
	}

	isURLValid = (url) => /^(((http(s)?:\/\/.)?(www\.)?)|(\*\.)?)[a-zA-Z0-9]+[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/.test(url);

	initFormModel() {
		super.initFormModel();
		this.formModel.addField('name', {
			getValue: () => this.state.stateForm.name,
			validation: {
				required: true,
				rules: [
					{
						assert: () => this.state.stateForm.operation !== RuleOperation.Add
							|| this.state.stateForm.name?.length <= 24,
						message: 'Name cannot be longer than 24 characters.',
					},
					{
						assert: () => !this.state.stateForm.name?.includes(' '),
						message: 'Name cannot contain spaces.',
					},
					{
						assert: () => this.state.stateForm.operation !== RuleOperation.Add
							|| /^(?![-_])[a-zA-Z][a-zA-Z-_]*(?<![-_])$/.test(this.state.stateForm.name ?? ''),
						message: 'Only letters (uppercase and lowercase), dashes, underscores are allowed. No trailing or beginning dashes or underscores.',
					}
				]
			},
		});

		this.formModel.addField('trafficDirection', {
			getValue: () => this.state.stateForm.trafficDirection,
			validation: {
				required: true,
			},
		});

		this.formModel.addField('destinationURLs', {
			getValue: () => this.state.stateForm.destinationURLs,
			validation: {
				required: false,
				rules: []
			}
		});

		// Override validation — values come from multi-select dropdowns
		this.formModel.fields.sourceIPs.validation.required =
			() => this.state.stateForm.trafficDirection?.name === TrafficDirection.Out.name;
		this.formModel.fields.sourceIPs.validation.rules = [];

		this.formModel.fields.destinationIPs.validation.required =
			() => this.state.stateForm.trafficDirection?.name === TrafficDirection.In.name
				|| this.state.stateForm.trafficDirection == null;
		this.formModel.fields.destinationIPs.validation.rules = [];

		this.formModel.fields.destinationPorts.validation.required =
			() => (this.state.stateForm.sourceIPsArray?.length > 0 || this.state.stateForm.destinationIPsArray?.length > 0);
		this.formModel.fields.destinationPorts.validation.rules = [];
	}

	getDefaultFormState() {
		return {
			operation: RuleOperation.Add,
			name: '',
			destinationURLs: '',
			trafficDirection: null,
			sourceIPsArray: [] as string[],
			destinationIPsArray: [] as string[],
			destinationPortsArray: [] as string[],
		};
	}

	getRegionOptions(): RuleRegion[] {
		const subToOnPremRegions = new RuleRegion().listByDirection(
			DoNotDecryptRequestsDirection.SubscriptionToOnPrem,
			this.props.validVirtualNetworks
		);
		const wwSubscriptionsNames = ['APPS_SharedSvcs_WW_DEV', 'APPS_SharedSvcs_WW_LAB', 'APPS_SharedSvcs_WW_PRD'];
		return wwSubscriptionsNames.includes(this.props.subscriptionRequestName)
			? subToOnPremRegions
			: subToOnPremRegions.filter(region => region !== RuleRegion.WW);
	}

	getFilteredMappings() {
		if (!this.props.completedPORRules?.hasCompletedPORRules || !this.props.completedPORRules?.ruleMappings) {
			return [];
		}
		const isOutbound = this.state.stateForm.trafficDirection?.name === TrafficDirection.Out.name;
		const dirFilter = isOutbound ? 'Out' : 'In';
		return this.props.completedPORRules.ruleMappings.filter(m => m.trafficDirection === dirFilter);
	}

	// ─── Multi-select option helpers ──────────────────────────────────────────
	sortIPOptions(options: { value: string; text: string }[]): { value: string; text: string }[] {
		return options.sort((a, b) => {
			try {
				const ipA = new Address4(a.value.split('/')[0]);
				const ipB = new Address4(b.value.split('/')[0]);
				const numA = ipA.toArray();
				const numB = ipB.toArray();
				for (let i = 0; i < 4; i++) {
					if (numA[i] !== numB[i]) return numA[i] - numB[i];
				}
				// When the IP addresses are the same, sort them in ascending order based on the prefix length.
				const prefixA = a.value.includes('/') ? parseInt(a.value.split('/')[1]) : 32;
				const prefixB = b.value.includes('/') ? parseInt(b.value.split('/')[1]) : 32;
				return prefixA - prefixB;
			} catch {
				return a.value.localeCompare(b.value);
			}
		});
	}

	getSourceIPOptions() {
		const mappings = this.getFilteredMappings();
		const isOutbound = this.state.stateForm.trafficDirection?.name === TrafficDirection.Out.name;

		if (isOutbound) {
			// Outbound: Source IPs are the first selection — show all unique source IPs
			const ips = [...new Set(mappings.map(m => m.sourceIP))];
			return this.sortIPOptions(ips.map(ip => ({ value: ip, text: ip })));
		} else {
			// Inbound: Source IPs depend on selected destination IPs
			const selectedDestIPs = this.state.stateForm.destinationIPsArray ?? [];
			if (selectedDestIPs.length === 0) return [];
			const ips = [...new Set(
				mappings
					.filter(m => selectedDestIPs.includes(m.destinationIP))
					.map(m => m.sourceIP)
			)];
			return this.sortIPOptions(ips.map(ip => ({ value: ip, text: ip })));
		}
	}

	getDestinationIPOptions() {
		const mappings = this.getFilteredMappings();
		const isOutbound = this.state.stateForm.trafficDirection?.name === TrafficDirection.Out.name;

		if (isOutbound) {
			// Outbound: Destination IPs depend on selected source IPs
			const selectedSrcIPs = this.state.stateForm.sourceIPsArray ?? [];
			if (selectedSrcIPs.length === 0) return [];
			const ips = [...new Set(
				mappings
					.filter(m => selectedSrcIPs.includes(m.sourceIP))
					.map(m => m.destinationIP)
			)];
			return this.sortIPOptions(ips.map(ip => ({ value: ip, text: ip })));
		} else {
			// Inbound: Destination IPs are the first selection
			const ips = [...new Set(mappings.map(m => m.destinationIP))];
			return this.sortIPOptions(ips.map(ip => ({ value: ip, text: ip })));
		}
	}

	getDestinationPortOptions() {
		const mappings = this.getFilteredMappings();
		const selectedSrcIPs = this.state.stateForm.sourceIPsArray ?? [];
		const selectedDstIPs = this.state.stateForm.destinationIPsArray ?? [];

		if (selectedSrcIPs.length === 0 || selectedDstIPs.length === 0) return [];

		const ports = [...new Set(
			mappings
				.filter(m => selectedSrcIPs.includes(m.sourceIP) && selectedDstIPs.includes(m.destinationIP))
				.map(m => m.destinationPort)
		)];
		return ports.sort((a, b) => Number(a) - Number(b)).map(port => ({ value: port, text: port }));
	}

	// ─── Checkbox change handlers ─────────────────────────────────────────────

	handleSourceIPsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		const current = this.state.stateForm.sourceIPsArray ?? [];
		const updated = current.includes(value)
			? current.filter(v => v !== value)
			: [...current, value];

		const form = { ...this.state.stateForm };
		form.sourceIPsArray = updated;
		form.sourceIPs = updated.join(';');
		// Outbound: changing source clears destination and ports
		if (this.state.stateForm.trafficDirection?.name === TrafficDirection.Out.name) {
			form.destinationIPsArray = [];
			form.destinationIPs = '';
		}
		form.destinationPortsArray = [];
		form.destinationPorts = '';
		this.setState({ stateForm: form });
	};

	handleDestinationIPsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		const current = this.state.stateForm.destinationIPsArray ?? [];
		const updated = current.includes(value)
			? current.filter(v => v !== value)
			: [...current, value];

		const form = { ...this.state.stateForm };
		form.destinationIPsArray = updated;
		form.destinationIPs = updated.join(';');
		// Inbound: changing destination clears source and ports
		if (this.state.stateForm.trafficDirection?.name !== TrafficDirection.Out.name) {
			form.sourceIPsArray = [];
			form.sourceIPs = '';
		}
		form.destinationPortsArray = [];
		form.destinationPorts = '';
		this.setState({ stateForm: form });
	};

	handleDestinationPortsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		const current = this.state.stateForm.destinationPortsArray ?? [];
		const updated = current.includes(value)
			? current.filter(v => v !== value)
			: [...current, value];

		const form = { ...this.state.stateForm };
		form.destinationPortsArray = updated;
		form.destinationPorts = updated.join(';');
		this.setState({ stateForm: form });
	};

	// ─── Traffic direction ────────────────────────────────────────────────────

	resetIPsValidations() {
		this.formModel.fields.sourceIPs.validation.rules = [];
		this.formModel.fields.destinationIPs.validation.rules = [];
	}

	loadIPsValidations(_direction) {
		this.resetIPsValidations();
		// No text-input validation needed — values come from dropdowns
	}

	handleTrafficDirection(event) {
		const form = { ...this.state.stateForm };
		form.trafficDirection = new TrafficDirection().fromValue(event.target.value);
		form.sourceIPsArray = [];
		form.sourceIPs = '';
		form.destinationIPsArray = [];
		form.destinationIPs = '';
		form.destinationPortsArray = [];
		form.destinationPorts = '';
		this.setState({ stateForm: form });
	}

	sortIPArray(ips: string[]): string[] {
		return [...ips].sort((a, b) => {
			try {
				const ipA = new Address4(a.split('/')[0]);
				const ipB = new Address4(b.split('/')[0]);
				const numA = ipA.toArray();
				const numB = ipB.toArray();
				for (let i = 0; i < 4; i++) {
					if (numA[i] !== numB[i]) return numA[i] - numB[i];
				}
				const prefixA = a.includes('/') ? parseInt(a.split('/')[1]) : 32;
				const prefixB = b.includes('/') ? parseInt(b.split('/')[1]) : 32;
				return prefixA - prefixB;
			} catch {
				return a.localeCompare(b);
			}
		});
	}

	sortPortArray(ports: string[]): string[] {
		return [...ports].sort((a, b) => Number(a) - Number(b));
	}

	// ─── Submit: match all combinations and create multiple rules ─────────────

	handleRequest = async (e) => {
		this.props.onClose(e);

		const mappings = this.getFilteredMappings();
		// Sort the values selected by a user
		const selectedSrcIPs = this.sortIPArray(this.state.stateForm.sourceIPsArray ?? []);
		const selectedDstIPs = this.sortIPArray(this.state.stateForm.destinationIPsArray ?? []);
		const selectedPorts = this.sortPortArray(this.state.stateForm.destinationPortsArray ?? []);

		const rules: DoNotDecryptRule[] = [];

		// Traverse in sequence of Source IP → Destination IP → Destination Port, only keep the tuples in mappings
		for (const srcIP of selectedSrcIPs) {
			for (const dstIP of selectedDstIPs) {
				for (const port of selectedPorts) {
					const exists = mappings.some(
						m => m.sourceIP === srcIP && m.destinationIP === dstIP && m.destinationPort === port
					);
					if (exists) {
						const model = this.formModel.create(DoNotDecryptRule);
						model.sourceIPs = srcIP;
						model.destinationIPs = dstIP;
						model.destinationPorts = port;
						rules.push(model);
					}
				}
			}
		}

		if (this.props.onConfirmMultiple) {
			this.props.onConfirmMultiple(rules);
		} else {
			rules.forEach(r => this.props.onConfirm(r));
		}

		this.resetState();
	};

	// ─── Render ───────────────────────────────────────────────────────────────

	render() {
		const isOutbound = this.state.stateForm.trafficDirection?.name === TrafficDirection.Out.name;
		const canEdit = this.formCanBeEdited();

		const srcOptions = this.getSourceIPOptions();
		const dstOptions = this.getDestinationIPOptions();
		const portOptions = this.getDestinationPortOptions();

		const srcChecked = this.state.stateForm.sourceIPsArray ?? [];
		const dstChecked = this.state.stateForm.destinationIPsArray ?? [];
		const portChecked = this.state.stateForm.destinationPortsArray ?? [];

		const srcLabel = srcChecked.length > 0 ? srcChecked.join(', ') : 'Select Source IP(s)';
		const dstLabel = dstChecked.length > 0 ? dstChecked.join(', ') : 'Select Destination IP(s)';
		const portLabel = portChecked.length > 0 ? portChecked.join(', ') : 'Select Destination Port(s)';

		const isFormValid = this.state.stateForm.trafficDirection != null
			&& this.state.stateForm.name?.length > 0
			&& this.state.stateForm.region != null
			&& srcChecked.length > 0
			&& dstChecked.length > 0
			&& portChecked.length > 0
			&& (this.state.stateForm.description?.trim().length ?? 0) >= 5;

		return (
			<Modal show={this.props.visible} onHide={() => this.props.onClose}>
				<Modal.Window className="ruleModal">
					<Modal.Window.Header>
						<Modal.Window.Header.Title>New Rule (Auto-match completed PORs)</Modal.Window.Header.Title>
						<Modal.Window.Header.CloseButton onClick={this.props.onClose}>
							<XFilledIcon size='small' />
						</Modal.Window.Header.CloseButton>
					</Modal.Window.Header>
					<Modal.Window.Body>
						<Grid variant="2-up">
							<Grid.Item>
								<TextInputField
									label="Rule Name"
									value={this.state.stateForm.name}
									validation={this.formModel.fields.name.validation}
									onChange={(event) => {
										this.handleStateFormChange('name', event.target.value);
									}}
									disabled={!canEdit}
								/>
							</Grid.Item>
							<Grid.Item>
								<SelectField
									{...this.stateFormHandler().region}
									options={this.getRegionOptions()}
									disabled={!canEdit}
								/>
							</Grid.Item>
						</Grid>
						<Grid variant="2-up">
							<Grid.Item>
								<SelectField
									label="Traffic Direction"
									value={this.state.stateForm.trafficDirection?.value}
									options={[
										{ text: 'Inbound (XOMNetwork -> Subscription)', value: TrafficDirection.In.value },
										{ text: 'Outbound (Subscription -> XOMNetwork)', value: TrafficDirection.Out.value },
									]}
									className="full-width"
									onChange={(event) => this.handleTrafficDirection(event)}
									validation={this.formModel.fields.trafficDirection.validation}
									disabled={!canEdit}
								/>
							</Grid.Item>
							<Grid.Item />
						</Grid>

						{this.state.stateForm.trafficDirection != null && (
							<Grid variant="2-up">
								<Grid.Item>
									{canEdit ? (
										<MultipleSelectField
											label="Source (IP)"
											value={srcLabel}
											options={srcOptions.map(o => ({
												...o,
												checked: srcChecked.includes(o.value)
											}))}
											onChange={this.handleSourceIPsChange}
											validation={this.formModel.fields.sourceIPs.validation}
											note="Select one or more source IPs."
											disabled={!canEdit || (isOutbound ? false : dstChecked.length === 0)}
										/>
									) : (
										<TextAreaField
											label="Source (IP)"
											value={srcChecked.join('\n')}
											disabled={true}
										/>
									)}
								</Grid.Item>
								<Grid.Item>
									{canEdit ? (
										<MultipleSelectField
											label="Destination (IP)"
											value={dstLabel}
											options={dstOptions.map(o => ({
												...o,
												checked: dstChecked.includes(o.value)
											}))}
											onChange={this.handleDestinationIPsChange}
											validation={this.formModel.fields.destinationIPs.validation}
											note="Select one or more destination IPs."
											disabled={!canEdit || (isOutbound ? srcChecked.length === 0 : false)}
										/>
									) : (
										<TextAreaField
											label="Destination (IP)"
											value={dstChecked.join('\n')}
											disabled={true}
										/>
									)}
								</Grid.Item>
							</Grid>
						)}

						{(srcChecked.length > 0 && dstChecked.length > 0) && (
							<Grid variant="2-up">
								<Grid.Item>
									{canEdit ? (
										<MultipleSelectField
											label="Destination Port"
											value={portLabel}
											options={portOptions.map(o => ({
												...o,
												checked: portChecked.includes(o.value)
											}))}
											onChange={this.handleDestinationPortsChange}
											validation={this.formModel.fields.destinationPorts.validation}
											note="Select one or more destination ports."
											disabled={!canEdit || portOptions.length === 0}
										/>
									) : (
										<TextAreaField
											label="Destination Port"
											value={portChecked.join('\n')}
											disabled={true}
										/>
									)}
								</Grid.Item>
								<Grid.Item />
							</Grid>
						)}

						<Grid variant="2-up">
							<Grid.Item>
								<TextAreaField
									{...this.stateFormHandler().description}
									label="Description"
									disabled={!canEdit}
								/>
							</Grid.Item>
						</Grid>
					</Modal.Window.Body>
					{!this.props.isReadOnly &&
						<Modal.Window.Footer>
							<Grid variant="2-up">
								<Grid.Item>
									<Button
										variant="primary"
										disabled={!isFormValid}
										onClick={this.handleRequest}
									>
										Create
									</Button>
								</Grid.Item>
								<Grid.Item>
									<Button
										variant="secondary"
										onClick={this.handleCancel}
									>
										Cancel
									</Button>
								</Grid.Item>
							</Grid>
						</Modal.Window.Footer>
					}
				</Modal.Window>
			</Modal>
		);
	}
}